import { StyleSheet, Text, TouchableOpacity, View, type StyleProp, type ViewStyle } from 'react-native';
import colors from '../../lib/colors';
import { useNewsletterSubscription } from '../../hooks/useNewsletterSubscription';
import Banner from '../ui/Banner';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';

interface NewsletterSubscribePromptProps {
    style?: StyleProp<ViewStyle>;
    align?: 'left' | 'center';
}

export default function NewsletterSubscribePrompt({
    style,
    align = 'left',
}: NewsletterSubscribePromptProps) {
    const { status, startSubscription, clearStatus, shouldRenderSubscribeButton } = useNewsletterSubscription();
    const isCentered = align === 'center';
    const isProcessing = status === 'processing';
    const shouldRenderBanner = status === 'processing' || status === 'subscribed' || status === 'error';

    if (!shouldRenderSubscribeButton && !shouldRenderBanner) {
        return null;
    }

    return (
        <View style={[styles.wrapper, isCentered && styles.wrapperCentered, style]}>
            {shouldRenderSubscribeButton && (
                <TouchableOpacity
                    style={[styles.subscribeButton, isProcessing && styles.subscribeButtonDisabled]}
                    activeOpacity={0.8}
                    onPress={startSubscription}
                    disabled={isProcessing}
                >
                    <Text style={styles.subscribeButtonText}>
                        {isProcessing ? <Trans id="newsletter.newslettersubscribeprompt.subscribing" comment="Conditional UI copy in components/newsletter/NewsletterSubscribePrompt.tsx">Subscribing...</Trans> : <Trans id="newsletter.newslettersubscribeprompt.subscribeToTheNewsletter" comment="Conditional UI copy in components/newsletter/NewsletterSubscribePrompt.tsx">Subscribe to the newsletter</Trans>}
                    </Text>
                </TouchableOpacity>
            )}

            {isProcessing && (
                <Banner
                    type="loading"
                    variant="subtle"
                    title={msg({ id: "newsletter.newslettersubscribeprompt.finalizingSubscription", message: "Finalizing subscription...", comment: "UI title in components/newsletter/NewsletterSubscribePrompt.tsx" })}
                    message={msg({ id: "newsletter.newslettersubscribeprompt.updatingYourNewsletterPreferences", message: "Updating your newsletter preferences.", comment: "UI message in components/newsletter/NewsletterSubscribePrompt.tsx" })}
                    style={[styles.banner, isCentered && styles.bannerCentered]}
                />
            )}

            {status === 'subscribed' && (
                <Banner
                    type="success"
                    variant="subtle"
                    title={msg({ id: "newsletter.newslettersubscribeprompt.youAreSubscribed", message: "You are subscribed", comment: "UI title in components/newsletter/NewsletterSubscribePrompt.tsx" })}
                    message={msg({ id: "newsletter.newslettersubscribeprompt.newNewsletterIssuesWillLandInYourInbox", message: "New newsletter issues will land in your inbox.", comment: "UI message in components/newsletter/NewsletterSubscribePrompt.tsx" })}
                    dismissable={true}
                    onDismiss={clearStatus}
                    style={[styles.banner, isCentered && styles.bannerCentered]}
                />
            )}

            {status === 'error' && (
                <Banner
                    type="error"
                    variant="subtle"
                    title={msg({ id: "newsletter.newslettersubscribeprompt.subscriptionNotConfirmed", message: "Subscription not confirmed", comment: "UI title in components/newsletter/NewsletterSubscribePrompt.tsx" })}
                    message={msg({ id: "newsletter.newslettersubscribeprompt.couldNotConfirmYourNewsletterSubscriptionRightNow", message: "Could not confirm your newsletter subscription right now.", comment: "UI message in components/newsletter/NewsletterSubscribePrompt.tsx" })}
                    action={{
                        label: msg({ id: "newsletter.newslettersubscribeprompt.tryAgain", message: "Try again", comment: "UI label in components/newsletter/NewsletterSubscribePrompt.tsx" }),
                        onPress: startSubscription,
                    }}
                    dismissable={true}
                    onDismiss={clearStatus}
                    style={[styles.banner, isCentered && styles.bannerCentered]}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    wrapper: {
        gap: 10,
    },
    wrapperCentered: {
        alignItems: 'center',
    },
    subscribeButton: {
        alignSelf: 'flex-start',
        backgroundColor: colors.primaryGreen,
        paddingHorizontal: 18,
        paddingVertical: 10,
        borderRadius: 999,
    },
    subscribeButtonDisabled: {
        opacity: 0.75,
    },
    subscribeButtonText: {
        color: '#ffffff',
        fontSize: 14,
        fontWeight: '600',
    },
    banner: {
        alignSelf: 'stretch',
        marginVertical: 0,
    },
    bannerCentered: {
        width: '100%',
    },
});
